import apiService from './api';
import { PaginationResponse } from './types';

export enum WithdrawalStatus {
  PENDING = 'pending',           // در انتظار تایید ادمین
  APPROVED = 'approved',         // تایید شده توسط ادمین - در حال پردازش
  PROCESSING = 'processing',     // در حال پردازش پرداخت
  COMPLETED = 'completed',       // تکمیل شده
  REJECTED = 'rejected',         // رد شده
  FAILED = 'failed'              // پرداخت ناموفق
}

export interface StatusChange {
  status: WithdrawalStatus;
  changedAt: string;
  changedBy?: string;
  reason?: string;
  notes?: string;
}

export interface WithdrawalRequest {
  _id: string;
  amount: number;
  netAmount?: number;
  commissionAmount?: number; // processingFee from backend
  commissionPercentage?: number; // calculated at backend
  // ✅ NEW (2026-05-04): bucket-aware withdrawal — تفکیک منبع برداشت
  // amount = salePortion + inspectionPortion. کارمزد فقط روی salePortion.
  salePortion?: number;
  inspectionPortion?: number;
  feePercentage?: number;     // snapshot نرخ مؤثر در زمان درخواست
  // ✅ NEW (2026-07-09): کمیسیون پلکانی — snapshot تفکیک پله‌ها + طرح لحظه‌ی ثبت
  feeBreakdown?: Array<{
    from: number;
    to: number | null;
    percent: number;
    portion: number;
    fee: number;
    // ✅ NEW (2026-08-16): ردیف‌های مبلغ‌ثابت — undefined در snapshot های قدیمی = percent
    kind?: 'percent' | 'fixed';
    amount?: number;
  }>;
  feeScheme?: {
    mode: 'flat' | 'tiers' | 'fixed' | 'fixed-tiers';
    percent?: number;
    tiers?: Array<{ upTo: number | null; percent: number }>;
    fixedAmount?: number;
    fixedTiers?: Array<{ upTo: number | null; amount: number }>;
    source:
      | 'store-tiers' | 'store-flat' | 'store-fixed' | 'store-fixed-tiers'
      | 'plan-tiers' | 'plan-flat' | 'plan-fixed' | 'plan-fixed-tiers'
      | 'global-tiers' | 'global-flat' | 'global-fixed' | 'global-fixed-tiers';
    planSubscriptionId?: string;
    planName?: string;
    planQuotaRemaining?: number | null;
  };
  // ✅ NEW (2026-07-09): چک زنده‌ی موجودی فروشنده (فقط در پاسخ جزئیات، وضعیت‌های غیرنهایی)
  balanceCheck?: {
    availableBalance: number;
    availableSaleBalance?: number | null;
    availableInspectionBalance?: number | null;
    /** کسری (قابل برداشت منفی) — اگر >۰ رزرو این درخواست دیگر پوشش ندارد */
    debt?: number;
    refundObligationsTotal?: number;
    sufficient: boolean;
    checkedAt: string;
  };
  // ✅ NEW (2026-07-09): خلاصه‌ی سابقه‌ی برداشت فروشنده (فقط در پاسخ جزئیات)
  sellerWithdrawalSummary?: {
    totalWithdrawn: number;
    completedCount: number;
    rejectedCount: number;
    failedCount: number;
  };
  status: WithdrawalStatus;
  requestedAt: string;
  processedAt?: string;
  processedBy?: string;
  approvedAt?: string;
  approvedBy?: string;
  bankInfo?: {
    accountNumber: string;
    bankName: string;
    accountHolder: string;
    iban?: string;
  };
  notes?: string;
  rejectionReason?: string;
  failureReason?: string;
  paymentReference?: string;
  referenceId?: string;
  statusHistory?: StatusChange[];
  /** واریز خودکار زرین‌پال (PayoutAdd) */
  payoutMode?: 'manual' | 'zarinpal';
  payout?: {
    provider: 'zarinpal';
    payoutId?: string;
    urlCode?: string;
    status: string; // submitted | queued | bank_account_pending | paid | failed
    zpStatus?: string;
    iban?: string;
    amount?: number;
    referenceId?: string;
    requestedAt?: string;
    checkedAt?: string;
    paidAt?: string;
    error?: string;
    attempts?: number;
  };
  sellerId: string;
  storeName?: string; // backward compat
  sellerName?: string; // backward compat
  sellerEmail?: string; // backward compat
  ownerName?: string; // list response fallback
  ownerEmail?: string; // list response fallback
  ownerInfo?: {
    name: string;
    email: string;
    phone?: string;
  };
  storeInfo?: {
    name: string;
    description?: string;
    commissionCustom?: number;
  };
  processedByInfo?: {
    name: string;
    email: string;
  };
  approvedByInfo?: {
    name: string;
    email: string;
  };
  referralInfo?: {
    active: boolean;
    percentage: number;
    baseFee: number;
    referralAmount: number;
    platformAmount: number;
    codeUsed?: string;
    /** true = مبلغ واقعاً پرداخت‌شده (snapshot لحظهٔ تکمیل)، نه محاسبهٔ لحظه‌ای */
    snapshot?: boolean;
    paidAt?: string | null;
    pending?: boolean;
    error?: string;
    referrer: null | {
      id: string;
      name: string;
      email: string;
    };
  };
}

export interface WithdrawalFilters {
  status?: WithdrawalStatus;
  sellerId?: string;
  minAmount?: number;
  maxAmount?: number;
  dateFrom?: string;
  dateTo?: string;
  /** جستجو روی نام فروشگاه/مالک، تلفن، ایمیل، شناسه پیگیری و مرجع پرداخت */
  search?: string;
}

interface WithdrawalStatBucket {
  count: number;
  totalAmount: number;
  avgAmount: number;
  /** جمع کارمزد پلتفرم این وضعیت (completed = کارمزد وصول‌شده) */
  totalFees?: number;
}

export interface WithdrawalStatistics {
  pending: WithdrawalStatBucket;
  approved: WithdrawalStatBucket;
  processing: WithdrawalStatBucket;
  completed: WithdrawalStatBucket;
  rejected: WithdrawalStatBucket;
  failed: WithdrawalStatBucket;
}

export interface WithdrawalResponse extends PaginationResponse<WithdrawalRequest> {
  statistics?: WithdrawalStatistics;
}

export class WithdrawalService {
  private baseUrl = '/admin/withdrawals';

  /**
   * Get all withdrawal requests with pagination and filters
   */
  public async getWithdrawalRequests(
    page: number = 1,
    limit: number = 10,
    filters: WithdrawalFilters = {}
  ): Promise<WithdrawalResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());

      // Add filters to query params
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.sellerId) queryParams.append('sellerId', filters.sellerId);
      if (filters.minAmount) queryParams.append('minAmount', filters.minAmount.toString());
      if (filters.maxAmount) queryParams.append('maxAmount', filters.maxAmount.toString());
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      if (filters.search) queryParams.append('search', filters.search);

      const response = await apiService.get<any>(`${this.baseUrl}?${queryParams.toString()}`);

      // Handle API response format
      if (response && response.status === 'success' && response.data) {
        return {
          items: response.data.withdrawalRequests || [],
          meta: response.data.pagination || {
            totalItems: 0,
            itemCount: 0,
            itemsPerPage: limit,
            totalPages: 0,
            currentPage: page
          },
          statistics: response.data.statistics
        };
      }

      return {
        items: [],
        meta: {
          totalItems: 0,
          itemCount: 0,
          itemsPerPage: limit,
          totalPages: 0,
          currentPage: page
        }
      };
    } catch (error) {
      console.error('Error fetching withdrawal requests:', error);
      throw error;
    }
  }

  /**
   * Get single withdrawal request details
   */
  public async getWithdrawalRequest(id: string): Promise<WithdrawalRequest | null> {
    try {
      const response = await apiService.get<any>(`${this.baseUrl}/${id}`);
      
      if (response && response.status === 'success' && response.data) {
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching withdrawal request:', error);
      throw error;
    }
  }

  /**
   * Approve withdrawal request (PENDING -> APPROVED)
   */
  public async approveWithdrawal(id: string, adminNotes?: string): Promise<any> {
    try {
      const response = await apiService.post<any>(`${this.baseUrl}/${id}/approve`, { adminNotes });
      
      if (response && response.status === 'success') {
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error approving withdrawal:', error);
      throw error;
    }
  }

  /**
   * Start processing withdrawal (APPROVED -> PROCESSING)
   */
  public async startProcessingWithdrawal(id: string, paymentReference: string, adminNotes?: string): Promise<any> {
    try {
      const response = await apiService.post<any>(`${this.baseUrl}/${id}/start-processing`, { 
        paymentReference, 
        adminNotes 
      });
      
      if (response && response.status === 'success') {
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error starting withdrawal processing:', error);
      throw error;
    }
  }

  /**
   * Complete withdrawal (PROCESSING -> COMPLETED)
   */
  /** پیکربندی واریز خودکار زرین‌پال */
  public async getPayoutConfig(): Promise<{ zarinpalEnabled: boolean; minPayoutToman: number; hours: string; withinHours: boolean; note: string } | null> {
    try {
      const response = await apiService.get<any>(`${this.baseUrl}/payout-config`);
      return response?.status === 'success' ? response.data : null;
    } catch { return null; }
  }

  /** واریز خودکار از زرین‌پال (از APPROVED یا PROCESSING) */
  public async payViaZarinpal(id: string, adminNotes?: string): Promise<{ message: string; payout?: any }> {
    const response = await apiService.post<any>(`${this.baseUrl}/${id}/pay-via-zarinpal`, { adminNotes });
    if (response && response.status === 'success') return { message: response.message, payout: response.data?.payout };
    throw new Error(response?.message || 'خطا در واریز خودکار زرین‌پال');
  }

  /** استعلام وضعیت واریز زرین‌پال */
  public async syncPayout(id: string): Promise<{ message: string; payout?: any; completed?: boolean }> {
    const response = await apiService.post<any>(`${this.baseUrl}/${id}/sync-payout`, {});
    return { message: response?.message, payout: response?.data?.payout, completed: response?.data?.completed };
  }

  public async completeWithdrawal(id: string, adminNotes?: string, paymentReference?: string): Promise<any> {
    try {
      const response = await apiService.post<any>(`${this.baseUrl}/${id}/complete`, { adminNotes, paymentReference });
      
      if (response && response.status === 'success') {
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error completing withdrawal:', error);
      throw error;
    }
  }

  /**
   * Mark withdrawal as failed (PROCESSING -> FAILED)
   */
  public async markWithdrawalFailed(id: string, failureReason: string, adminNotes?: string): Promise<any> {
    try {
      const response = await apiService.post<any>(`${this.baseUrl}/${id}/mark-failed`, { 
        failureReason, 
        adminNotes 
      });
      
      if (response && response.status === 'success') {
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error marking withdrawal as failed:', error);
      throw error;
    }
  }

  /**
   * Reject withdrawal request (PENDING -> REJECTED)
   */
  public async rejectWithdrawal(id: string, rejectionReason: string, adminNotes?: string): Promise<any> {
    try {
      const response = await apiService.post<any>(`${this.baseUrl}/${id}/reject`, { 
        rejectionReason, 
        adminNotes 
      });
      
      if (response && response.status === 'success') {
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error rejecting withdrawal:', error);
      throw error;
    }
  }

  /**
   * Get withdrawal statistics
   */
  public async getWithdrawalStatistics(dateFrom?: string, dateTo?: string): Promise<WithdrawalStatistics> {
    try {
      const queryParams = new URLSearchParams();
      if (dateFrom) queryParams.append('dateFrom', dateFrom);
      if (dateTo) queryParams.append('dateTo', dateTo);

      const response = await apiService.get<any>(`${this.baseUrl}/stats?${queryParams.toString()}`);
      
      if (response && response.status === 'success' && response.data) {
        return response.data;
      }
      
      return {
        pending: { count: 0, totalAmount: 0, avgAmount: 0 },
        approved: { count: 0, totalAmount: 0, avgAmount: 0 },
        processing: { count: 0, totalAmount: 0, avgAmount: 0 },
        completed: { count: 0, totalAmount: 0, avgAmount: 0 },
        rejected: { count: 0, totalAmount: 0, avgAmount: 0 },
        failed: { count: 0, totalAmount: 0, avgAmount: 0 }
      };
    } catch (error) {
      console.error('Error fetching withdrawal statistics:', error);
      throw error;
    }
  }

  /**
   * Export withdrawals to Excel
   */
  public async exportWithdrawals(filters: WithdrawalFilters = {}): Promise<WithdrawalRequest[]> {
    try {
      const queryParams = new URLSearchParams();

      // Add filters to query params
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);

      const response = await apiService.get<any>(`${this.baseUrl}/export?${queryParams.toString()}`);
      
      if (response && response.status === 'success' && response.data) {
        return response.data;
      }
      
      return [];
    } catch (error) {
      console.error('Error exporting withdrawals:', error);
      throw error;
    }
  }

  // Helper methods for formatting and display
  public getStatusText(status: WithdrawalStatus): string {
    const statusTexts = {
      [WithdrawalStatus.PENDING]: 'در انتظار تایید',
      [WithdrawalStatus.APPROVED]: 'تایید شده',
      [WithdrawalStatus.PROCESSING]: 'در حال پردازش',
      [WithdrawalStatus.COMPLETED]: 'تکمیل شده',
      [WithdrawalStatus.REJECTED]: 'رد شده',
      [WithdrawalStatus.FAILED]: 'ناموفق'
    };
    return statusTexts[status] || status;
  }

  public getStatusColor(status: WithdrawalStatus): string {
    const statusColors = {
      [WithdrawalStatus.PENDING]: 'warning',
      [WithdrawalStatus.APPROVED]: 'info',
      [WithdrawalStatus.PROCESSING]: 'primary',
      [WithdrawalStatus.COMPLETED]: 'success',
      [WithdrawalStatus.REJECTED]: 'error',
      [WithdrawalStatus.FAILED]: 'error'
    };
    return statusColors[status] || 'default';
  }

  public formatPrice(amount: number): string {
    return new Intl.NumberFormat('fa-IR').format(amount);
  }

  public formatDate(dateString: string): string {
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  }
}

export default new WithdrawalService(); 